import { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Link, TextBody, TextCaption, TextDisplay1, TextHeadline } from '@cbhq/cds-web/typography';

import { getStaticConfig } from ':support/config';
import {
  getCookie,
  GSSC_COOKIE,
  LOGIN_SESSION_COOKIE,
  UNIFIED_SESSION_MANAGER_COOKIE,
} from ':support/utils/cookies';

// Entry-gateway mounted path for initiating unified login flow
const ENTRY_GATEWAY_LOGIN_PATH = '/login';

// Conditionally mount this as "/login", which is the route that EGW uses to
// initiate the login flow
export function LocalLogin() {
  const { deployedHost } = getStaticConfig();

  const [sessionCookie, setSessionCookie] = useState<string>(
    getCookie(UNIFIED_SESSION_MANAGER_COOKIE),
  );
  const [loginSessionCookie, setLoginSessionCookie] = useState<string>(
    getCookie(LOGIN_SESSION_COOKIE),
  );
  const [gsscCookie, setGsscCookie] = useState<string>(getCookie(GSSC_COOKIE));

  useEffect(function setPageTitle() {
    const originalTitle = document.title;
    document.title = 'Local dev - login';
    return () => {
      document.title = originalTitle;
    };
  });

  const handleGsscCookieChanged: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => setGsscCookie(e.target.value),
    [setGsscCookie],
  );

  const handleLoginSessionCookieChanged: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setLoginSessionCookie(e.target.value);
    },
    [setLoginSessionCookie],
  );

  const handleSessionCookieChanged: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => setSessionCookie(e.target.value),
    [setSessionCookie],
  );

  const handleSignin = useCallback(() => {
    document.cookie = `${GSSC_COOKIE}=${gsscCookie}; path=/; domain=localhost`;
    document.cookie = `${LOGIN_SESSION_COOKIE}=${loginSessionCookie}; path=/; domain=localhost;`;
    document.cookie = `${UNIFIED_SESSION_MANAGER_COOKIE}=${sessionCookie}; path=/; domain=localhost`;

    // [ROUT-868]: Support a redirect URL
    window.location.assign('/');
  }, [gsscCookie, loginSessionCookie, sessionCookie]);

  return (
    <Box padding={4} minWidth="100vw" minHeight="100vh" background="bg">
      <VStack gap={2}>
        <TextDisplay1 as="h1" paddingBottom={2} display="block">
          Local Dev Signin
        </TextDisplay1>

        <TextBody as="p" display="block">
          In order to login for local development, you will need to login to a deployed environment
          and copy your session cookie.
        </TextBody>

        <TextBody as="p" paddingBottom={2} display="block">
          This process transfers authentication cookies from a deployed environment to your
          localhost domain for local development authentication.
        </TextBody>

        <TextHeadline as="h2" display="block">
          Steps
        </TextHeadline>

        <VStack padding={2} paddingTop={0}>
          <TextBody as="p" paddingBottom={1} display="block">
            1) Log in at{' '}
            <Link
              href={`${deployedHost}${ENTRY_GATEWAY_LOGIN_PATH}`}
              target="_blank"
              rel="noreferrer"
            >
              {`${deployedHost}${ENTRY_GATEWAY_LOGIN_PATH}` ||
                'Hmm, missing the DEPLOYED_HOST ENV VAR'}
            </Link>
          </TextBody>

          <TextBody as="p" display="block">
            2) In the Chrome debugger, find and copy your{' '}
            <TextCaption as="span">{UNIFIED_SESSION_MANAGER_COOKIE}</TextCaption>,{' '}
            <TextCaption as="span">{GSSC_COOKIE}</TextCaption>, and{' '}
            <TextCaption as="span">{LOGIN_SESSION_COOKIE}</TextCaption> cookies.
          </TextBody>

          <VStack gap={1} padding={2}>
            <TextInput
              value={gsscCookie}
              onChange={handleGsscCookieChanged}
              label={GSSC_COOKIE}
              compact
            />
            <TextInput
              value={loginSessionCookie}
              onChange={handleLoginSessionCookieChanged}
              label={LOGIN_SESSION_COOKIE}
              compact
            />
            <TextInput
              value={sessionCookie}
              onChange={handleSessionCookieChanged}
              label={UNIFIED_SESSION_MANAGER_COOKIE}
              compact
            />

            <Button compact onClick={handleSignin}>
              Signin
            </Button>
          </VStack>
        </VStack>
      </VStack>
    </Box>
  );
}
